import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';

interface TimerComponentProps {
  seconds: number;
  onComplete?: () => void;
  autoLaunch?: boolean;
  resetKey?: string | number; // Used to force reset when timeline step changes
}

export const TimerComponent: React.FC<TimerComponentProps> = ({ seconds, onComplete, autoLaunch = false, resetKey }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [isRunning, setIsRunning] = useState(false);

  // Reset timer when resetKey changes (timeline step changes)
  useEffect(() => {
    setTimeLeft(seconds);
    // setIsRunning(false);
  }, [resetKey, seconds]);

  // Auto launch effect
  useEffect(() => {
    if (autoLaunch) {
      setIsRunning(true);
    }
  }, [autoLaunch]);

  const handleComplete = useCallback(() => {
    setIsRunning(false);
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleComplete();
    }
  }, [timeLeft, handleComplete]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{t('timer.timer')}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsRunning(!isRunning)}
            className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 transition-colors"
          >
            {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => {
              setTimeLeft(seconds);
              // setIsRunning(false);
            }}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-3xl font-bold text-gray-900 mb-2">
          {formatTime(timeLeft)}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
            style={{ width: `${(timeLeft / seconds) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};
